import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { UserExperiments } from '@acp/shared';
import { getActiveExperiments } from '../service';

function useExperiment() {
    const queryClient = useQueryClient();
    const query = useQuery({
        queryKey: ['activeExperiments'],
        queryFn: () => getActiveExperiments(),
    });

    const hasExperiment = (experimentName: keyof UserExperiments) => {
        if (query.data === undefined) return false;
        return query.data[experimentName] === true;
    };

    const invalidate = () => queryClient.invalidateQueries({
        queryKey: ['activeExperiments'],
    });

    return {
        hasExperiment,
        userExperiments: query.data,
        isLoading: query.isLoading,
        invalidate,
    };
}

function usePrefetchExperiments() {
    const queryClient = useQueryClient();

    useEffect(() => {
        void queryClient.prefetchQuery({
            queryKey: ['activeExperiments'],
            queryFn: () => getActiveExperiments(),
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

function useSuspendExperimentBlock({
    shouldBlock,
    onBlock,
}: {
    shouldBlock: (experiments: UserExperiments) => boolean;
    onBlock: () => void;
}) {
    const query = useSuspenseQuery({
        queryKey: ['activeExperiments'],
        queryFn: () => getActiveExperiments(),
    });
    const isInvalid = useMemo(() => shouldBlock(query.data), [query.data, shouldBlock]);

    useEffect(() => {
        if (!isInvalid) return;
        onBlock();
    }, [isInvalid, onBlock]);

    return {
        isBlocked: query.isLoading || isInvalid,
        userExperiments: query.data,
    };
}

export { usePrefetchExperiments, useExperiment, useSuspendExperimentBlock };
